/**
 * Drill-in tool timeline — pure derivation over a durable `wf:events:<runId>` replay.
 *
 * llm-provider-interop-kimi-k3.md §6.2: ordered per-tool-call detail lives ONLY in the durable
 * Redis stream (drill-in replay), never the live store/socket. That stream trims at
 * `EVENTS_MAXLEN` (workflow-registry.service.ts), so a long run's surviving replay is a PARTIAL
 * history — #M1 requires the UI to say so explicitly ("timeline truncated"), never to present a
 * trimmed head as the complete run. This module is a LEAF (imports only the wire types, no I/O,
 * no clock) so SE-COST-05 drives it with golden fixtures and no infrastructure.
 *
 * Truncation detection: the FIRST event ever appended for a run is always its `run_started`
 * (the emitter appends every normalized event in order, and nothing precedes the launch). So a
 * replay whose first surviving event is NOT `run_started` has provably lost its head to the
 * MAXLEN trim. An EMPTY replay is "unknown", not "truncated" — absence of history is not proof
 * of trimming (Redis may be down, the run may be foreign), so it derives empty + untruncated.
 */
import type { WorkflowProgressEvent } from './workflow-progress';

export interface ToolTimelineEntry {
  /** Envelope timestamp of the underlying event (ISO-8601, server clock). */
  at: string;
  kind: 'run_started' | 'run_finished' | 'node_started' | 'node_finished' | 'tool';
  /** Owning node for node/tool entries; absent on run-level markers. */
  nodeId?: string;
  /** kind 'tool' only — the sighted tool name (from `node_progress.lastToolName`). */
  toolName?: string;
  /** kind 'tool' only — the LAST tick's elapsed seconds for a collapsed long-running call. */
  elapsedSeconds?: number;
  /** kind 'node_finished' only. */
  outcome?: string;
  /** kind 'run_finished' only. */
  status?: string;
  /** kind 'node_started' only — the seeded label. */
  label?: string;
}

export interface ToolTimeline {
  entries: ToolTimelineEntry[];
  /** #M1: the stream head was trimmed at EVENTS_MAXLEN — this replay is a partial history. */
  truncated: boolean;
  /** Raw surviving replay length (before collapse) — feeds the "truncated at N events" copy. */
  totalEvents: number;
}

/**
 * Derive the ordered tool timeline from a raw replay. Tolerant by construction: rows that are
 * not objects or carry no known `kind` are SKIPPED (one corrupt payload must never blank the
 * whole drill-in), and consecutive progress ticks of the SAME (nodeId, toolName) collapse into
 * ONE entry keeping the last tick's `elapsedSeconds` — three ticks of one long-running Bash are
 * one call, not three.
 */
export function deriveToolTimeline(events: readonly unknown[]): ToolTimeline {
  const entries: ToolTimelineEntry[] = [];
  let firstKind: string | null = null;

  for (const raw of events) {
    if (!raw || typeof raw !== 'object') continue;
    const e = raw as Partial<WorkflowProgressEvent> & Record<string, unknown>;
    const kind = typeof e.kind === 'string' ? e.kind : null;
    if (!kind) continue;
    if (firstKind === null) firstKind = kind;
    const at = typeof e.at === 'string' ? e.at : '';

    switch (kind) {
      case 'run_started':
        entries.push({ at, kind: 'run_started' });
        break;
      case 'run_finished':
        entries.push({ at, kind: 'run_finished', status: (e as { status?: string }).status });
        break;
      case 'node_started': {
        const node = (e as { node?: { nodeId?: string; label?: string } }).node;
        entries.push({ at, kind: 'node_started', nodeId: node?.nodeId, label: node?.label });
        break;
      }
      case 'node_finished':
        entries.push({
          at,
          kind: 'node_finished',
          nodeId: (e as { nodeId?: string }).nodeId,
          outcome: (e as { outcome?: string }).outcome,
        });
        break;
      case 'node_progress': {
        const nodeId = (e as { nodeId?: string }).nodeId;
        const toolName = (e as { lastToolName?: string }).lastToolName;
        const elapsedSeconds = (e as { elapsedSeconds?: number }).elapsedSeconds;
        if (!toolName) break; // heartbeat tick with no tool sighting — not a timeline entry
        const prev = entries[entries.length - 1];
        if (prev && prev.kind === 'tool' && prev.nodeId === nodeId && prev.toolName === toolName) {
          // Same call, later tick — collapse, keeping the freshest elapsed + timestamp.
          prev.at = at || prev.at;
          if (elapsedSeconds !== undefined) prev.elapsedSeconds = elapsedSeconds;
          break;
        }
        entries.push({
          at,
          kind: 'tool',
          nodeId,
          toolName,
          ...(elapsedSeconds !== undefined ? { elapsedSeconds } : {}),
        });
        break;
      }
      default:
        // run_updated / node_retrying / node_waiting / node_wait_resolved / run_snapshot —
        // lifecycle noise for the TIMELINE view; deliberately not rendered.
        break;
    }
  }

  return {
    entries,
    truncated: firstKind !== null && firstKind !== 'run_started',
    totalEvents: events.length,
  };
}
